import React, { memo, useCallback, useMemo } from 'react';
import type { ProductCatalog as ProductCatalogType, ProductOffer } from '@segmently/schema-models/v2';
import type { TemplateVariables } from '../../../utils/contentResolver';
import type { PreviewMode } from '../../../types/previewMode';
import { mapFrameLayoutStyles } from '../../../utils/styleMapper';
import { getSchemaTestId } from '../../../utils/testIdUtils';
import { resolveProductTemplateVariables } from '../../../utils/paymentTokens';
import { useElementAnimation, mergeAnimationClassName } from '../../../animations';
import { ProductOfferItem } from '../ProductOfferItem';
import type { ProductOfferEventContext } from '../ProductOfferItem';
import styles from './ProductCatalog.module.css';

// The schema types `productId` as a plain, non-unique `string` with no
// pattern constraint (ruling R-10, 2026-09-26) — a live catalog can
// legitimately carry the same productId on more than one offer item, AND an
// author can legitimately give some OTHER item a productId that happens to
// look exactly like a key this module would otherwise generate. React
// requires sibling keys to be unique, so any collision — with another raw
// id, a disambiguated key, or a fallback key — reproduces the same "two
// children with the same key" console error this module exists to prevent.
//
// `MISSING_PRODUCT_ID_KEY_PREFIX` is only a first-guess candidate for the
// missing-productId fallback; `reserveGeneratedKey` below is what actually
// guarantees no collision, against adversarial input included (task review,
// fix round 1, Important #1).
const MISSING_PRODUCT_ID_KEY_PREFIX = 'pc-idx-';

/**
 * Builds a React key per catalog item, aligned 1:1 with `items`:
 * - unique productId, or a repeated productId's first occurrence -> the
 *   bare productId (no remount/behaviour change for the common case of
 *   catalogs with unique ids — this is always free, see below);
 * - a productId's 2nd, 3rd… occurrence -> a generated, escaped key based on
 *   `${productId}#${occurrence}`;
 * - empty/missing productId -> a generated, escaped key based on
 *   `${MISSING_PRODUCT_ID_KEY_PREFIX}${index}`.
 *
 * "Escaped" means: collected against every RAW productId in the catalog
 * (not just the one being disambiguated) and every key already generated —
 * so a generated key can never collide with a real, distinct, single-
 * occurrence productId that happens to look like the generated pattern
 * (e.g. items `['x', 'x', 'x#2']`, or `['', 'pc-idx-0']`), nor with another
 * generated key. A bare first-occurrence key needs no such check: every
 * generated key explicitly avoids the full raw-id set, so nothing could
 * already have taken that exact string.
 */
function buildProductCatalogItemKeys(items: ProductOffer[]): string[] {
  const rawProductIds = new Set(items.map((item) => item.productId).filter(Boolean));
  const usedKeys = new Set<string>();

  // Deterministically escalates `candidate` until it matches neither a raw
  // productId nor an already-generated key. Termination is guaranteed:
  // `rawProductIds` and `usedKeys` are both finite (bounded by items.length
  // each), and every escalation appends a distinct, strictly-growing suffix,
  // so within at most `rawProductIds.size + usedKeys.size + 1` attempts the
  // candidate must fall outside both finite sets.
  function reserveGeneratedKey(candidate: string): string {
    let key = candidate;
    let attempt = 0;
    while (rawProductIds.has(key) || usedKeys.has(key)) {
      attempt += 1;
      key = `${candidate}~${attempt}`;
    }
    usedKeys.add(key);
    return key;
  }

  const occurrenceByProductId = new Map<string, number>();
  return items.map((item, index) => {
    const productId = item.productId;
    if (!productId) {
      return reserveGeneratedKey(`${MISSING_PRODUCT_ID_KEY_PREFIX}${index}`);
    }
    const occurrence = (occurrenceByProductId.get(productId) ?? 0) + 1;
    occurrenceByProductId.set(productId, occurrence);
    if (occurrence === 1) {
      usedKeys.add(productId);
      return productId;
    }
    return reserveGeneratedKey(`${productId}#${occurrence}`);
  });
}

export interface ProductCatalogProps {
  catalog: ProductCatalogType | undefined;
  locale: string;
  variables?: TemplateVariables;
  itemVariablesMap?: Record<string, TemplateVariables>;
  selectedIndex?: number | null;
  onSelect?: (item: ProductOffer, index: number) => void;
  className?: string;
  'data-testid'?: string;
  editPath?: string;
  previewMode?: PreviewMode;
}

export const ProductCatalog = memo(function ProductCatalog({
  catalog,
  locale,
  variables,
  itemVariablesMap,
  selectedIndex,
  onSelect,
  className,
  'data-testid': testId,
  editPath,
  previewMode,
}: ProductCatalogProps): React.ReactElement | null {
  // ⚠️ Rules of Hooks: hooks run on EVERY render — the empty-catalog
  // early-return sits below them (a catalog can materialize mid-session via
  // theme/editor writes; a pre-hook return flips hook count and crashes).
  const catalogAnim = useElementAnimation(editPath);
  const handleItemSelect = useCallback((context: ProductOfferEventContext) => {
    if (onSelect) {
      onSelect(context.item, context.index);
    }
  }, [onSelect]);
  const itemKeys = useMemo(
    () => buildProductCatalogItemKeys(catalog?.items ?? []),
    [catalog?.items],
  );

  if (!catalog || !catalog.items || catalog.items.length === 0) {
    return null;
  }

  const isHorizontal = catalog.viewKind === 'Horizontal';
  const layout = isHorizontal ? 'horizontal' : 'vertical';

  const containerClassName = mergeAnimationClassName(
    [
      styles.productCatalog,
      isHorizontal ? styles.grid : '',
      className,
    ].filter(Boolean).join(' '),
    catalogAnim,
  );

  const containerStyle = {
    gap: catalog.appearance?.itemsSpacing,
    ...mapFrameLayoutStyles(catalog.layout),
    ...catalogAnim.style,
  };

  const schemaTestId = testId ?? getSchemaTestId(editPath);
  const controlledSelectedIndex =
    Number.isInteger(selectedIndex) &&
    (selectedIndex as number) >= 0 &&
    (selectedIndex as number) < catalog.items.length
      ? selectedIndex as number
      : null;
  const effectiveSelectedIndex = controlledSelectedIndex ??
    catalog.items.findIndex((item) => item.isSelected === true);

  return (
    <div
      className={containerClassName}
      style={containerStyle}
      data-anim={catalogAnim['data-anim']}
      data-testid={schemaTestId}
      data-edit-path={editPath}
    >
      {catalog.items.map((item, index) => {
        // Retain authored tags in editor/canvas previews. Only live purchase surfaces
        // blank unresolved payment tokens.
        const itemVariables = resolveProductTemplateVariables({
          productId: item.productId,
          previewMode,
          itemVariablesMap,
          variables,
        });

        return (
          <ProductOfferItem
            key={itemKeys[index]}
            item={item}
            productLayout={catalog.productLayout}
            index={index}
            locale={locale}
            variables={itemVariables}
            selected={effectiveSelectedIndex === index}
            catalogAppearance={catalog.appearance}
            selectedCatalogAppearance={catalog.selectedAppearance}
            onSelect={handleItemSelect}
            data-testid={schemaTestId ? `${schemaTestId}-item-${index}` : undefined}
            editPath={editPath ? `${editPath}.items.${index}` : undefined}
            previewMode={previewMode}
            layout={layout}
            staggerIndex={index}
          />
        );
      })}
    </div>
  );
});

ProductCatalog.displayName = 'ProductCatalog';
